import { useState } from 'react'
import type { Sistema, User, Setor } from '@/models'
import { useSortableTable } from '../hooks'
import { SortableHeader } from './SortableHeader'
import { ConfirmModal } from '@/components/Modal/ConfirmModal'
import { Loading } from '@/components/Loading'
import { FaServer, FaEdit, FaTrash, FaEye, FaBox, FaUserShield } from 'react-icons/fa'
import { formatCurrency } from '../../../utils/formatter'

interface ProcessPermissions {
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
  canView: boolean
}

interface SistemsTableProps {
  sistemas: Sistema[]
  permissions: ProcessPermissions
  isLoading?: boolean
  onEdit: (sistema: Sistema) => void
  onDelete: (sistema: Sistema) => void
  onView?: (sistema: Sistema) => void
  searchQuery?: string
  usuarios?: User[]
  setores?: Setor[]
}

const CategoriaBadge = ({ categoria }: { categoria: string }) => {
  const config: Record<string, { color: string; label: string }> = {
    'Software de Negócio': { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', label: 'Negócio' },
    'Banco de Dados': { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Banco de Dados' },
    'Sistema Operacional': { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200', label: 'SO' },
    'Web': { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200', label: 'Web' },
  }

  const { color, label } = config[categoria] || { color: 'bg-gray-100 text-gray-800', label: categoria }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {label}
    </span>
  )
}

 export const SistemsTable = ({ 
  sistemas, 
  permissions,
  isLoading = false, 
  onEdit, 
  onDelete, 
  onView,
  searchQuery = '',
  usuarios = [],
  setores = [],
}: SistemsTableProps) => {
  const resolveNomeSetor = (id: number) =>
    setores.find(s => s.id === id)?.nome_setor ?? `Setor #${id}`

  const resolveNomeUsuario = (id: number) =>
    usuarios.find(u => u.id === id)?.name ?? `Usuário #${id}`
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; sistema: Sistema | null }>({
    isOpen: false,
    sistema: null
  })
  const [isDeleting, setIsDeleting] = useState(false)

  // Hook de ordenação
  const { sortedData, sortConfig, requestSort } = useSortableTable(sistemas)

  const handleDeleteClick = (sistema: Sistema) => {
    setDeleteModal({ isOpen: true, sistema })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteModal.sistema) return
    
    try {
      setIsDeleting(true)
      await onDelete(deleteModal.sistema)
      setDeleteModal({ isOpen: false, sistema: null })
    } catch (error) {
      console.error('Erro ao excluir sistema:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, sistema: null })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700">
        <div className="p-8 text-center space-y-4">
          <Loading message="Carregando sistemas..." />
          <p className="text-gray-600 dark:text-gray-400">Aguarde enquanto buscamos os dados</p>
        </div>
      </div>
    )
  }

  // Empty state
  if (sortedData.length === 0 && !isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700">
        <div className="p-12 text-center">
          <div className="inline-flex p-4 rounded-full bg-gray-100 dark:bg-neutral-700 mb-4">
            <FaServer className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {searchQuery ? 'Nenhum sistema encontrado' : 'Nenhum sistema cadastrado'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchQuery 
              ? 'Tente ajustar sua busca ou limpar os filtros.' 
              : 'Comece cadastrando seu primeiro sistema de TI.'
            }
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700 overflow-hidden flex flex-col h-[calc(100vh-230px)]">
        <div className="overflow-x-auto overflow-y-auto flex-1">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
            <thead className="bg-gray-50 dark:bg-neutral-900 sticky top-0 z-10">
              <tr>
                <SortableHeader
                  label="Nome"
                  sortKey="nome"
                  currentSort={sortConfig}
                  onSort={requestSort}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                />
                <SortableHeader
                  label="Categoria"
                  sortKey="categoria"
                  currentSort={sortConfig}
                  onSort={requestSort}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                />
                <SortableHeader
                  label="Versão"
                  sortKey="versao_atual"
                  currentSort={sortConfig}
                  onSort={requestSort}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                />
                <SortableHeader
                  label="Proprietário"
                  sortKey="proprietario"
                  currentSort={sortConfig}
                  onSort={requestSort}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell"
                />
                <SortableHeader
                  label="Gestor Técnico"
                  sortKey="gestor_tecnico"
                  currentSort={sortConfig}
                  onSort={requestSort}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell"
                />
                <SortableHeader
                  label="Licenças"
                  sortKey="quantidade_licencas"
                  currentSort={sortConfig}
                  onSort={requestSort}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell"
                />
                <SortableHeader
                  label="Custo Anual"
                  sortKey="custo_anual"
                  currentSort={sortConfig}
                  onSort={requestSort}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell"
                />
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Módulos
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Perfis
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky right-0 bg-gray-50 dark:bg-neutral-900">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-800 divide-y divide-gray-200 dark:divide-neutral-700">
              {sortedData.map((sistema) => (
                <tr 
                  key={sistema.id}
                  className="hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition-colors"
                >
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {sistema.nome}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {sistema.fornecedor}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <CategoriaBadge categoria={sistema.categoria} />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {sistema.versao_atual}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap hidden xl:table-cell">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {sistema.proprietario?.nome_setor ?? resolveNomeSetor(sistema.proprietario_fk)}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap hidden xl:table-cell">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {sistema.gestor_tecnico?.name ?? resolveNomeUsuario(sistema.gestor_tecnico_fk)}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-gray-100 hidden lg:table-cell">
                    {sistema.quantidade_licencas}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 hidden lg:table-cell">
                    {formatCurrency(sistema.custo_anual)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      <FaBox className="h-3 w-3" />
                      {sistema.modulos?.length || 0}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                      <FaUserShield className="h-3 w-3" />
                      {sistema.perfis?.length || 0}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white dark:bg-neutral-800">
                    <div className="flex items-center justify-end gap-2">
                      {permissions.canView && onView && (
                        <button
                          onClick={() => onView(sistema)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          title="Visualizar"
                        >
                          <FaEye className="h-4 w-4" />
                        </button>
                      )}
                      {permissions.canEdit && (
                        <button
                          onClick={() => onEdit(sistema)}
                          className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300 transition-colors p-2 rounded-md hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                          title="Editar"
                        >
                          <FaEdit className="h-4 w-4" />
                        </button>
                      )}
                      {permissions.canDelete && (
                        <button
                          onClick={() => handleDeleteClick(sistema)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="Excluir"
                        >
                          <FaTrash className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        type="danger"
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o sistema "${deleteModal.sistema?.nome}"?`}
        details="Esta ação não pode ser desfeita. Todos os módulos e perfis relacionados também serão desativados."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={isDeleting}
      />
    </>
  )
}
